import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { parseSystemOverviewMarkdown } from "~/features/projectTop/pageView/utils/markdownParser";
import { DocumentApi } from "~/functions/api/document";
import { ProcessLockApi } from "~/functions/api/processLock";
import type { RootState } from "~/store";

interface Table {
  headers: string[];
  rows: string[][];
}

interface SystemOverviewData {
  概要: string;
  背景: string[];
  その他: string[];
  メリット?: string[];
  導入するポイント?: string[];
  特徴?: string[];
  主な機能: Table;
  業務フロー: string[];
  目的: string[];
  ユースケーステーブル: Table;
}

export interface SystemProposalData {
  [key: string]: {
    システム概要: SystemOverviewData;
  };
}

// デフォルト値の定義
const DEFAULT_TABLE: Table = {
  headers: [],
  rows: [],
};

const DEFAULT_SYSTEM_PROPOSAL_DATA: SystemOverviewData = {
  概要: "",
  背景: [],
  その他: [],
  特徴: [],
  主な機能: DEFAULT_TABLE,
  業務フロー: [],
  目的: [],
  ユースケーステーブル: DEFAULT_TABLE,
};

const DEFAULT_PARSED_DATA: SystemProposalData = {
  提案システム: {
    システム概要: DEFAULT_SYSTEM_PROPOSAL_DATA,
  },
};

export interface SystemProposalState {
  // レガシー構造化データ（後方互換性のため保持）
  data: SystemProposalData;
  copyData: SystemProposalData;
  firstKey: string;
  // メイン状態
  isEditable: boolean;
  isLoading: boolean;
  error: string | null;
  documentInfo: {
    createdAt: string;
    modifiedAt: string;
    modifiedBy?: {
      display_name: string;
    };
  } | null;
  // マークダウン形式のコンテンツ（メイン）
  markdownContent: string;
  initialMarkdownContent: string;
  sources: { type: string; id: string }[];
}

const initialState: SystemProposalState = {
  data: DEFAULT_PARSED_DATA,
  copyData: DEFAULT_PARSED_DATA,
  firstKey: "",
  isEditable: false,
  isLoading: false,
  error: null,
  documentInfo: null,
  markdownContent: "",
  initialMarkdownContent: "",
  sources: [],
};

// セレクターの定義
export const selectDocumentId = (state: RootState): string | undefined => {
  return state.auth.currentProject.outputs?.["system-proposal"]?.id;
};

export const fetchSystemProposal = createAsyncThunk(
  "systemProposal/fetch",
  async (documentIdArg: string | undefined, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentUser, currentProject } = state.auth;
      // URLパラメータから取得したIDを優先、なければセレクターから取得
      const documentId = documentIdArg || selectDocumentId(state);

      if (!documentId) {
        return rejectWithValue("システム概要書が見つかりません");
      }

      const documentApi = new DocumentApi();
      const response = await documentApi.get(
        currentUser.organization_id,
        currentProject.id,
        documentId,
      );
      if (response && "content" in response) {
        // コンテンツがマークダウン形式かJSON形式かを判断
        const getContentByFormat = () => {
          try {
            const parsedContent = JSON.parse(response.content);
            // JSON形式で特定の構造を持つ場合は構造化データとして扱う
            // それ以外の場合はマークダウンとして扱う
            const hasSystemProposalStructure =
              parsedContent &&
              typeof parsedContent === "object" &&
              !parsedContent.detail &&
              Object.keys(parsedContent).length > 0 &&
              Object.values(parsedContent).some(
                (val: unknown) => val && typeof val === "object" && "システム概要" in val,
              );

            if (hasSystemProposalStructure) {
              // 構造化データとして扱う
              return {
                markdownContent: "",
                data: parsedContent,
              };
            } else {
              // マークダウンとして扱う（JSONでパースできたが構造が異なる場合）
              const parsed = parseSystemOverviewMarkdown(response.content);
              if (parsed) {
                return {
                  markdownContent: response.content,
                  data: {
                    [parsed.systemName]: {
                      システム概要: parsed.data,
                    },
                  },
                };
              }
              return {
                markdownContent: response.content,
                data: null,
              };
            }
          } catch (_error) {
            // JSON形式でない場合、マークダウンからパースを試みる
            const parsed = parseSystemOverviewMarkdown(response.content);
            if (parsed) {
              return {
                markdownContent: response.content,
                data: {
                  [parsed.systemName]: {
                    システム概要: parsed.data,
                  },
                },
              };
            }
            return {
              markdownContent: response.content,
              data: null,
            };
          }
        };

        const { markdownContent, data } = getContentByFormat();

        return {
          data: data || DEFAULT_PARSED_DATA,
          markdownContent: markdownContent
            .replace(/\\n/g, "\n")
            .replace(/\\"/g, '"')
            .replace(/^"|"$/g, "")
            .replace(/\\n/g, "\n")
            .replace(/\\"/g, ""),
          documentInfo: {
            createdAt: response.created_at || "",
            modifiedAt: response.modified_at || "",
            modifiedBy: response.modified_by,
          },
          sources: response.sources ?? [],
        };
      }
      return rejectWithValue("データの取得に失敗しました");
    } catch (_error) {
      return rejectWithValue("データの取得中にエラーが発生しました");
    }
  },
);

export const acquireLock = createAsyncThunk(
  "systemProposal/acquireLock",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentUser, currentProject } = state.auth;
      const documentId = selectDocumentId(state);

      if (!documentId) {
        return rejectWithValue("システム概要書が見つかりません");
      }

      const processLockApi = new ProcessLockApi();
      const response = await processLockApi.lock(currentUser.organization_id, currentProject.id, {
        type: "system-proposal",
        mode: "edit",
      });
      if (response === null) {
        return rejectWithValue("編集権限を取得できませんでした");
      }
      return true;
    } catch (_error) {
      return rejectWithValue("編集権限の取得中にエラーが発生しました");
    }
  },
);

export const releaseLock = createAsyncThunk(
  "systemProposal/releaseLock",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentUser, currentProject } = state.auth;
      const documentId = selectDocumentId(state);

      if (!documentId) {
        return rejectWithValue("システム概要書が見つかりません");
      }

      const processLockApi = new ProcessLockApi();
      const response = await processLockApi.unlock(currentUser.organization_id, currentProject.id, {
        type: "system-proposal",
        mode: "edit",
      });
      if (response === null) {
        return rejectWithValue("編集権限の解放に失敗しました");
      }
      return true;
    } catch (_error) {
      return rejectWithValue("編集権限の解放中にエラーが発生しました");
    }
  },
);

export const updateSystemProposal = createAsyncThunk(
  "systemProposal/update",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentUser, currentProject } = state.auth;
      const { data, markdownContent } = state.systemProposalReducer;
      const documentId = selectDocumentId(state);

      if (!documentId) {
        return rejectWithValue("システム概要書が見つかりません");
      }

      const documentApi = new DocumentApi();

      // マークダウンコンテンツがある場合はそちらを優先
      const contentToSave =
        markdownContent && markdownContent.trim() !== "" ? markdownContent : JSON.stringify(data);

      const response = await documentApi.updateContent(
        currentUser.organization_id,
        currentProject.id,
        documentId,
        {
          content: contentToSave,
        },
      );
      if (response === null) {
        return rejectWithValue("データの更新に失敗しました");
      }

      // データの更新が成功したら、ロックを解除
      await dispatch(releaseLock());

      return true;
    } catch (_error) {
      return rejectWithValue("データの更新中にエラーが発生しました");
    }
  },
);

export const updateSystemProposalContent = createAsyncThunk(
  "systemProposal/updateContent",
  async (content: string, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { currentUser, currentProject } = state.auth;
      const documentId = selectDocumentId(state);

      if (!documentId) {
        return rejectWithValue("システム概要書が見つかりません");
      }

      const documentApi = new DocumentApi();
      const response = await documentApi.updateContent(
        currentUser.organization_id,
        currentProject.id,
        documentId,
        { content },
      );

      if (response === null) {
        return rejectWithValue("データの更新に失敗しました");
      }

      return true;
    } catch (_error) {
      return rejectWithValue("データの更新中にエラーが発生しました");
    }
  },
);

export const resetData = createAsyncThunk("systemProposal/resetData", async (_, { getState }) => {
  const state = getState() as RootState;
  const { copyData } = state.systemProposalReducer;
  return copyData || DEFAULT_PARSED_DATA;
});

const systemProposalSlice = createSlice({
  name: "systemProposal",
  initialState,
  reducers: {
    setData: (state, action: PayloadAction<SystemProposalData>) => {
      state.data = {
        ...DEFAULT_PARSED_DATA,
        ...action.payload,
      };
      state.firstKey = Object.keys(action.payload)[0] || "提案システム";
    },
    updateArrayField: (
      state,
      action: PayloadAction<{
        field: keyof SystemOverviewData;
        value: string[];
      }>,
    ) => {
      if (state.data && state.firstKey && state.data[state.firstKey]) {
        const systemData = state.data[state.firstKey]!;
        const field = action.payload.field;
        if (field === "主な機能" || field === "ユースケーステーブル") {
          systemData.システム概要[field] = {
            headers: systemData.システム概要[field].headers,
            rows: action.payload.value as unknown as string[][],
          };
        } else if (field !== "概要") {
          systemData.システム概要[field] = action.payload.value;
        }
      }
    },
    updateTable: (
      state,
      action: PayloadAction<{
        field: "主な機能" | "ユースケーステーブル";
        value: Table;
      }>,
    ) => {
      if (state.data && state.firstKey && state.data[state.firstKey]) {
        const systemData = state.data[state.firstKey]!;
        systemData.システム概要[action.payload.field] = action.payload.value;
      }
    },
    addMainFunctionRow: (state) => {
      if (state.data && state.firstKey && state.data[state.firstKey]) {
        const systemData = state.data[state.firstKey]!;
        const headers = systemData.システム概要.主な機能.headers;
        const emptyRow = headers.map(() => "");
        systemData.システム概要.主な機能.rows = [
          ...systemData.システム概要.主な機能.rows,
          emptyRow,
        ];
      }
    },
    addUsecaseTableRow: (state) => {
      if (state.data && state.firstKey && state.data[state.firstKey]) {
        const systemData = state.data[state.firstKey]!;
        const headers = systemData.システム概要.ユースケーステーブル.headers;
        const emptyRow = headers.map((header) => (header === "実装方法" ? "基本機能" : ""));
        systemData.システム概要.ユースケーステーブル.rows = [
          ...systemData.システム概要.ユースケーステーブル.rows,
          emptyRow,
        ];
      }
    },
    deleteMainFunctionRow: (state, action: PayloadAction<number>) => {
      if (state.data && state.firstKey && state.data[state.firstKey]) {
        const systemData = state.data[state.firstKey]!;
        const rows = systemData.システム概要.主な機能.rows;
        systemData.システム概要.主な機能.rows = rows.filter((_, index) => index !== action.payload);
      }
    },
    deleteUsecaseTableRow: (state, action: PayloadAction<number>) => {
      if (state.data && state.firstKey && state.data[state.firstKey]) {
        const systemData = state.data[state.firstKey]!;
        const rows = systemData.システム概要.ユースケーステーブル.rows;
        systemData.システム概要.ユースケーステーブル.rows = rows.filter(
          (_, index) => index !== action.payload,
        );
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    updateMarkdownContent: (state, action: PayloadAction<string>) => {
      state.markdownContent = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSystemProposal.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSystemProposal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.copyData = action.payload.data;
        state.markdownContent = action.payload.markdownContent;
        state.initialMarkdownContent = action.payload.markdownContent;
        state.firstKey = Object.keys(action.payload.data)[0] || "提案システム";
        state.sources = action.payload.sources;
        if (action.payload.documentInfo.modifiedBy) {
          state.documentInfo = {
            createdAt: action.payload.documentInfo.createdAt,
            modifiedAt: action.payload.documentInfo.modifiedAt,
            modifiedBy: {
              display_name: action.payload.documentInfo.modifiedBy.display_name,
            },
          };
        } else {
          state.documentInfo = {
            createdAt: action.payload.documentInfo.createdAt,
            modifiedAt: action.payload.documentInfo.modifiedAt,
          };
        }
      })
      .addCase(fetchSystemProposal.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(acquireLock.fulfilled, (state) => {
        state.isEditable = true;
        state.copyData = JSON.parse(JSON.stringify(state.data));
        state.initialMarkdownContent = state.markdownContent;
      })
      .addCase(acquireLock.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(releaseLock.fulfilled, (state) => {
        state.isEditable = false;
      })
      .addCase(releaseLock.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(updateSystemProposal.fulfilled, (state) => {
        state.isEditable = false;
      })
      .addCase(updateSystemProposal.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(resetData.fulfilled, (state, action) => {
        state.data = action.payload;
        state.markdownContent = state.initialMarkdownContent;
      })
      .addCase(updateSystemProposalContent.fulfilled, (_state) => {
        // isEditable は releaseLock.fulfilled で false になる
      })
      .addCase(updateSystemProposalContent.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  setData,
  updateArrayField,
  updateTable,
  addMainFunctionRow,
  addUsecaseTableRow,
  deleteMainFunctionRow,
  deleteUsecaseTableRow,
  clearError,
  updateMarkdownContent,
} = systemProposalSlice.actions;

export default systemProposalSlice.reducer;
